import React, { useState, useEffect } from 'react';

import API from './config';

const COLORS = {
  bleu: '#1E3A5F', brun: '#7A4E2D',
  modules: { devis:'#F97316', factures:'#16A34A', agenda:'#7C3AED' }
};

function Section({ titre, couleur, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{marginBottom:'16px', border:`1px solid #E5E7EB`, borderRadius:'8px', overflow:'hidden'}}>
      <div onClick={() => setOpen(!open)} style={{background: couleur, color:'white', padding:'12px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <span style={{fontWeight:'700', fontSize:'14px'}}>{titre}</span>
        <span style={{fontSize:'18px'}}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{padding:'16px'}}>{children}</div>}
    </div>
  );
}

const ACHAT_VIDE = { designation: '', fournisseur: '', quantite: 1, prix_unitaire_ttc: 0, date_achat: '', notes: '' };

export default function FicheAffaire({ apiFetch, affaire, clients, peutEcrire=true, refreshKey=0, onRetour, onImprimerDevis, onImprimerFacture, onEmettre, onOuvrirDevis, onNouveauDevis, onFacturer, onAcompte, onEncaisser, onCloturer, onEnvoyerEmail, onModified }) {
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [achats, setAchats] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formEdit, setFormEdit] = useState({ titre: affaire.titre || '', description: affaire.description || '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [achatEnCours, setAchatEnCours] = useState(null); // null | 'nouveau' | id de l'achat édité
  const [formAchat, setFormAchat] = useState(ACHAT_VIDE);
  const [savingAchat, setSavingAchat] = useState(false);
  const client = clients.find(c => c.id === affaire.client_id);

  const chargerAchats = () => {
    apiFetch(`${API}/achats/`).then(r=>r.json()).then(a => setAchats(a.filter(x => x.affaire_id === affaire.id))).catch(()=>{});
  };

  const ouvrirNouvelAchat = () => { setFormAchat(ACHAT_VIDE); setAchatEnCours('nouveau'); };
  const ouvrirEditionAchat = (a) => {
    setFormAchat({
      designation: a.designation, fournisseur: a.fournisseur || '',
      quantite: a.quantite, prix_unitaire_ttc: a.prix_unitaire_ttc,
      date_achat: a.date_achat ? a.date_achat.slice(0, 10) : '', notes: a.notes || '',
    });
    setAchatEnCours(a.id);
  };
  const annulerAchat = () => { setAchatEnCours(null); setFormAchat(ACHAT_VIDE); };

  const enregistrerAchat = async () => {
    if (!formAchat.designation.trim()) return;
    setSavingAchat(true);
    const payload = {
      affaire_id: affaire.id,
      designation: formAchat.designation.trim(),
      fournisseur: formAchat.fournisseur.trim() || null,
      quantite: formAchat.quantite,
      prix_unitaire_ttc: formAchat.prix_unitaire_ttc,
      date_achat: formAchat.date_achat ? new Date(formAchat.date_achat).toISOString() : null,
      notes: formAchat.notes.trim() || null,
    };
    const estEdition = achatEnCours !== 'nouveau';
    const url = estEdition ? `${API}/achats/${achatEnCours}` : `${API}/achats/`;
    const r = await apiFetch(url, { method: estEdition ? 'PUT' : 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (r.ok) { chargerAchats(); annulerAchat(); }
    else alert('Erreur lors de l\'enregistrement de l\'achat');
    setSavingAchat(false);
  };

  const supprimerAchat = async (a) => {
    if (!window.confirm(`Supprimer l'achat « ${a.designation} » (${parseFloat(a.total_ttc).toFixed(2)} €) ?`)) return;
    const r = await apiFetch(`${API}/achats/${a.id}`, { method: 'DELETE' });
    if (r.ok) chargerAchats();
  };

  useEffect(() => {
    setFormEdit({ titre: affaire.titre || '', description: affaire.description || '' });
    setEditMode(false);
    setEditError('');
  }, [affaire.id, affaire.titre, affaire.description]);

  const enregistrerModif = async () => {
    const titre = (formEdit.titre || '').trim();
    if (!titre) { setEditError('Le titre est obligatoire.'); return; }
    setSavingEdit(true);
    setEditError('');
    try {
      const r = await apiFetch(`${API}/affaires/${affaire.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre, description: formEdit.description || null })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setEditError(err.detail || 'Erreur lors de la modification.');
        return;
      }
      setEditMode(false);
      if (onModified) onModified();
    } catch (e) {
      setEditError('Erreur réseau.');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    apiFetch(`${API}/devis/`).then(r=>r.json()).then(d => setDevis(d.filter(x => x.affaire_id === affaire.id))).catch(()=>{});
    apiFetch(`${API}/factures/`).then(r=>r.json()).then(f => setFactures(f.filter(x => x.affaire_id === affaire.id))).catch(()=>{});
    apiFetch(`${API}/agenda/`).then(r=>r.json()).then(e => setEvenements(e.filter(x => x.affaire_id === affaire.id))).catch(()=>{});
    chargerAchats();
    // apiFetch est passé en prop et change à chaque rendu parent ; le mettre en dep créerait une boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affaire.id, refreshKey]);

  const s = {
    badge: (s) => ({ display:'inline-block', padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'600',
      background: s==='ouverte' ? '#DCFCE7' : s==='emise'||s==='facture' ? '#DBEAFE' : s==='brouillon' ? '#FEF9C3' : s==='annulee' ? '#FEE2E2' : s==='payee'||s==='accepte' ? '#EDE9FE' : s==='partiellement_payee' ? '#FEF3C7' : '#F3F4F6',
      color: s==='ouverte' ? '#16A34A' : s==='emise'||s==='facture' ? '#1D4ED8' : s==='brouillon' ? '#CA8A04' : s==='annulee' ? '#DC2626' : s==='payee'||s==='accepte' ? '#7C3AED' : s==='partiellement_payee' ? '#D97706' : '#6B7280'
    }),
    th: (c) => ({ background: c, color:'white', padding:'10px 14px', textAlign:'left', fontSize:'13px' }),
    td: { padding:'10px 14px', borderBottom:'1px solid #F3F4F6', fontSize:'14px' },
    btnSm: (c) => ({ background: c||'#E5E7EB', color: c?'white':'#374151', border:'none', padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', marginRight:'4px' }),
    info: { fontSize:'14px', color:'#374151', marginBottom:'6px' }
  };

  const totalFacture = factures.reduce((s,f) => s + parseFloat(f.montant_ht||0), 0);
  const totalPaye = factures.reduce((s,f) => s + (parseFloat(f.montant_ht||0) - parseFloat(f.solde_restant||0)), 0);
  const restantDu = totalFacture - totalPaye;
  const totalAchats = achats.reduce((s,a) => s + parseFloat(a.total_ttc||0), 0);
  const margeBrute = totalFacture - totalAchats;

  // Détection automatique : toutes les factures soldées (payée ou annulée par avoir)
  // et au moins une facture
  const peutCloturer = affaire.statut === 'ouverte'
    && factures.length > 0
    && factures.every(f => f.statut === 'payee' || f.statut === 'annulee')
    && devis.every(d => d.statut_global !== 'en_cours');

  return (
    <div>
      <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px', flexWrap:'wrap'}}>
        <button onClick={onRetour} style={{background:'#E5E7EB', border:'none', padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontSize:'14px'}}>← Retour</button>
        <h2 style={{margin:0, color:COLORS.bleu, fontSize:'20px'}}>{affaire.reference} — {affaire.titre}</h2>
        <span style={s.badge(affaire.statut)}>{affaire.statut}</span>
        <div style={{marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center'}}>
          {peutEcrire && !editMode && affaire.statut !== 'cloturee' && (
            <button
              onClick={() => setEditMode(true)}
              style={{background:'#6B7280', color:'white', border:'none', padding:'8px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600'}}>
              ✏️ Modifier
            </button>
          )}
          {peutEcrire && peutCloturer && onCloturer && !editMode && (
            <button
              onClick={() => { if(window.confirm('Clôturer cette affaire ? Elle passera en archive et n\'apparaîtra plus dans les affaires en cours.')) onCloturer(affaire.id); }}
              style={{background:'#16A34A', color:'white', border:'none', padding:'8px 18px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', fontWeight:'700'}}>
              🏁 Clôturer l'affaire
            </button>
          )}
          {affaire.statut === 'cloturee' && (
            <span style={{fontSize:'13px', color:'#16A34A', fontWeight:'600', padding:'8px 14px', background:'#DCFCE7', borderRadius:'8px'}}>
              🏁 Affaire clôturée
            </span>
          )}
        </div>
      </div>

      {/* Bandeau clôture automatique suggérée */}
      {peutEcrire && peutCloturer && onCloturer && (
        <div style={{background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:'8px', padding:'12px 16px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'12px'}}>
          <span style={{fontSize:'20px'}}>🏁</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:'700', color:'#16A34A', fontSize:'14px'}}>Toutes les factures sont soldées</div>
            <div style={{fontSize:'13px', color:'#6B7280'}}>Ce chantier peut être clôturé et archivé.</div>
          </div>
        </div>
      )}

      {/* Carte client */}
      {client && (
        <div style={{background:'white', borderRadius:'8px', padding:'14px 18px', boxShadow:'0 2px 6px rgba(0,0,0,0.08)', marginBottom:'16px', borderLeft:`4px solid ${COLORS.bleu}`, display:'flex', alignItems:'center', gap:'16px'}}>
          <span style={{fontSize:'24px'}}>{client.est_professionnel ? '🏢' : '👤'}</span>
          <div style={{flex:1}}>
            <div style={{fontSize:'13px', color:'#6B7280', marginBottom:'2px'}}>Client</div>
            <div style={{fontWeight:'700', color:COLORS.bleu, fontSize:'16px'}}>{client.nom} {client.prenom||''}</div>
            {client.telephone && <div style={{fontSize:'13px', color:'#374151', marginTop:'2px'}}>📞 {client.telephone}</div>}
          </div>
          {affaire.adresse_chantier && (
            <div style={{fontSize:'13px', color:'#6B7280', textAlign:'right'}}>
              <div style={{fontWeight:'600', color:'#374151'}}>📍 Chantier</div>
              <div>{affaire.adresse_chantier}</div>
              <div>{affaire.ville_chantier||''}</div>
            </div>
          )}
        </div>
      )}

      <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px', marginBottom:'20px'}}>
        <div style={{background:'white', borderRadius:'8px', padding:'16px', boxShadow:'0 2px 6px rgba(0,0,0,0.08)', borderTop:`3px solid ${COLORS.modules.factures}`}}>
          <div style={{fontSize:'11px', color:'#6B7280', textTransform:'uppercase', marginBottom:'4px'}}>Facturé HT</div>
          <div style={{fontSize:'22px', fontWeight:'700', color:COLORS.modules.factures}}>{totalFacture.toFixed(2)} €</div>
        </div>
        <div style={{background:'white', borderRadius:'8px', padding:'16px', boxShadow:'0 2px 6px rgba(0,0,0,0.08)', borderTop:`3px solid #16A34A`}}>
          <div style={{fontSize:'11px', color:'#6B7280', textTransform:'uppercase', marginBottom:'4px'}}>Encaissé HT</div>
          <div style={{fontSize:'22px', fontWeight:'700', color:'#16A34A'}}>{totalPaye.toFixed(2)} €</div>
        </div>
        <div style={{background:'white', borderRadius:'8px', padding:'16px', boxShadow:'0 2px 6px rgba(0,0,0,0.08)', borderTop:`3px solid #DC2626`}}>
          <div style={{fontSize:'11px', color:'#6B7280', textTransform:'uppercase', marginBottom:'4px'}}>Restant dû HT</div>
          <div style={{fontSize:'22px', fontWeight:'700', color:'#DC2626'}}>{restantDu.toFixed(2)} €</div>
        </div>
        <div style={{background:'white', borderRadius:'8px', padding:'16px', boxShadow:'0 2px 6px rgba(0,0,0,0.08)', borderTop:`3px solid ${COLORS.brun}`}} title="Facturé HT − achats matériaux TTC">
          <div style={{fontSize:'11px', color:'#6B7280', textTransform:'uppercase', marginBottom:'4px'}}>Marge brute</div>
          <div style={{fontSize:'22px', fontWeight:'700', color: margeBrute >= 0 ? COLORS.brun : '#DC2626'}}>{margeBrute.toFixed(2)} €</div>
        </div>
      </div>

      <Section titre="📋 Informations générales" couleur={COLORS.bleu}>
        {editMode ? (
          <div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'12px'}}>
              <div>
                <p style={s.info}><strong>Référence :</strong> {affaire.reference} <span style={{fontSize:'11px', color:'#9CA3AF'}}>(non modifiable)</span></p>
                <label style={{display:'block', fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'4px'}}>Titre *</label>
                <input
                  type="text"
                  value={formEdit.titre}
                  onChange={e => setFormEdit({...formEdit, titre: e.target.value})}
                  style={{width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', marginBottom:'12px', boxSizing:'border-box'}}
                />
                <label style={{display:'block', fontSize:'13px', fontWeight:'600', color:'#374151', marginBottom:'4px'}}>Description</label>
                <textarea
                  value={formEdit.description}
                  onChange={e => setFormEdit({...formEdit, description: e.target.value})}
                  rows={4}
                  style={{width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit'}}
                />
              </div>
              <div>
                <p style={s.info}><strong>Client :</strong> {client?.nom} {client?.prenom||''} <span style={{fontSize:'11px', color:'#9CA3AF'}}>(non modifiable)</span></p>
                <p style={s.info}><strong>Téléphone :</strong> {client?.telephone||'—'}</p>
                <p style={s.info}><strong>Ville chantier :</strong> {affaire.ville_chantier||'—'}</p>
                <p style={s.info}><strong>Adresse :</strong> {affaire.adresse_chantier||'—'}</p>
              </div>
            </div>
            {editError && <div style={{color:'#DC2626', fontSize:'13px', marginBottom:'10px'}}>{editError}</div>}
            <div style={{display:'flex', gap:'8px', justifyContent:'flex-end'}}>
              <button
                onClick={() => { setEditMode(false); setFormEdit({titre: affaire.titre||'', description: affaire.description||''}); setEditError(''); }}
                disabled={savingEdit}
                style={{background:'#E5E7EB', color:'#374151', border:'none', padding:'8px 16px', borderRadius:'6px', cursor: savingEdit?'not-allowed':'pointer', fontSize:'13px'}}>
                ✕ Annuler
              </button>
              <button
                onClick={enregistrerModif}
                disabled={savingEdit}
                style={{background:'#16A34A', color:'white', border:'none', padding:'8px 16px', borderRadius:'6px', cursor: savingEdit?'not-allowed':'pointer', fontSize:'13px', fontWeight:'600'}}>
                {savingEdit ? 'Enregistrement…' : '💾 Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px'}}>
            <div>
              <p style={s.info}><strong>Référence :</strong> {affaire.reference}</p>
              <p style={s.info}><strong>Titre :</strong> {affaire.titre}</p>
              <p style={s.info}><strong>Statut :</strong> <span style={s.badge(affaire.statut)}>{affaire.statut}</span></p>
              <p style={s.info}><strong>Description :</strong> {affaire.description||'—'}</p>
            </div>
            <div>
              <p style={s.info}><strong>Client :</strong> {client?.nom} {client?.prenom||''}</p>
              <p style={s.info}><strong>Téléphone :</strong> {client?.telephone||'—'}</p>
              <p style={s.info}><strong>Ville chantier :</strong> {affaire.ville_chantier||'—'}</p>
              <p style={s.info}><strong>Adresse :</strong> {affaire.adresse_chantier||'—'}</p>
            </div>
          </div>
        )}
      </Section>

      <Section titre={`📋 Devis (${devis.length})`} couleur={COLORS.modules.devis}>
        <div style={{display:'flex', justifyContent:'flex-end', marginBottom:'10px'}}>
          {peutEcrire && onNouveauDevis && <button style={{...s.btnSm(COLORS.modules.devis), fontSize:'13px', padding:'6px 14px'}} onClick={onNouveauDevis}>+ Nouveau devis</button>}
        </div>
        {devis.length === 0
          ? <p style={{color:'#9CA3AF', fontSize:'14px'}}>Aucun devis pour cette affaire</p>
          : <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                {['Référence','Statut','Montant HT','Date','Actions'].map(h => <th key={h} style={s.th(COLORS.modules.devis)}>{h}</th>)}
              </tr></thead>
              <tbody>
                {devis.map((d,i) => (
                  <tr key={d.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                    <td style={{...s.td, fontWeight:'600'}}>{d.reference}</td>
                    <td style={s.td}><span style={s.badge(d.statut_global)}>{d.statut_global}</span></td>
                    <td style={s.td}>{parseFloat(d.montant_ht||0).toFixed(2)} €</td>
                    <td style={s.td}>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                    <td style={s.td}>
                      {onOuvrirDevis && <button style={s.btnSm(COLORS.modules.devis)} onClick={() => onOuvrirDevis(d.id)}>Ouvrir →</button>}
                      {peutEcrire && d.statut_global === 'accepte' && onAcompte && <button style={s.btnSm('#0369A1')} onClick={() => onAcompte(d.id)}>📥 Acompte</button>}
                      {peutEcrire && d.statut_global === 'accepte' && onFacturer && <button style={s.btnSm('#16A34A')} onClick={() => onFacturer(d.id)}>📄 Facturer</button>}
                      {d.statut_global === 'facture' && <span style={{fontSize:'11px', color:'#16A34A', fontWeight:'600'}}>✅ Facturé</span>}
                      <button style={s.btnSm('#6B7280')} onClick={() => onImprimerDevis(d.id)}>🖨️ PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </Section>

      <Section titre={`📄 Factures (${factures.length})`} couleur={COLORS.modules.factures}>
        {factures.length === 0 ? <p style={{color:'#9CA3AF', fontSize:'14px'}}>Aucune facture pour cette affaire</p> :
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead><tr>
              {['Numéro','Type','Montant HT','Solde','Statut','Actions'].map(h => <th key={h} style={s.th(COLORS.modules.factures)}>{h}</th>)}
            </tr></thead>
            <tbody>
              {factures.map((f,i) => (
                <tr key={f.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                  <td style={{...s.td, fontWeight:'600'}}>{f.numero}</td>
                  <td style={s.td}>
                    {f.type === 'acompte'
                      ? <span style={{display:'inline-block', padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'700', background:'#E0F2FE', color:'#0369A1'}}>Acompte</span>
                      : <span style={{display:'inline-block', padding:'2px 8px', borderRadius:'12px', fontSize:'11px', fontWeight:'600', background:'#F3F4F6', color:'#6B7280'}}>Solde</span>
                    }
                  </td>
                  <td style={s.td}>{parseFloat(f.montant_ht).toFixed(2)} €</td>
                  <td style={s.td}>{parseFloat(f.solde_restant).toFixed(2)} €</td>
                  <td style={s.td}><span style={s.badge(f.statut)}>{f.statut}</span></td>
                  <td style={s.td}>
                    {peutEcrire && f.statut === 'brouillon' && <button style={s.btnSm(COLORS.modules.factures)} onClick={() => onEmettre(f.id)}>📤 Émettre</button>}
                    {peutEcrire && (f.statut === 'emise' || f.statut === 'partiellement_payee') && onEncaisser && (
                      <button style={s.btnSm('#16A34A')} onClick={() => onEncaisser(f)}>💰 Encaisser</button>
                    )}
                    {onEnvoyerEmail && (f.statut === 'emise' || f.statut === 'partiellement_payee') && (
                      <button style={s.btnSm('#0EA5E9')} onClick={() => onEnvoyerEmail(f.id)}>📧</button>
                    )}
                    <button style={s.btnSm('#6B7280')} onClick={() => onImprimerFacture(f.id)}>🖨️ PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Section>

      <Section titre={`🧾 Achats matériaux (${achats.length})`} couleur={COLORS.brun}>
        <div style={{display:'flex', justifyContent:'flex-end', marginBottom:'10px'}}>
          {peutEcrire && achatEnCours === null && <button style={{...s.btnSm(COLORS.brun), fontSize:'13px', padding:'6px 14px'}} onClick={ouvrirNouvelAchat}>+ Ajouter un achat</button>}
        </div>

        {achatEnCours !== null && (
          <div style={{background:'#FAF7F4', border:`1px solid ${COLORS.brun}`, borderRadius:'8px', padding:'14px', marginBottom:'14px'}}>
            <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr', gap:'10px', marginBottom:'10px'}}>
              <div>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Désignation *</label>
                <input value={formAchat.designation} onChange={e => setFormAchat({...formAchat, designation:e.target.value})} placeholder="Ex: Panneaux MDF 19mm" style={{width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Fournisseur</label>
                <input value={formAchat.fournisseur} onChange={e => setFormAchat({...formAchat, fournisseur:e.target.value})} placeholder="Ex: Point P" style={{width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Quantité</label>
                <input type="number" step="0.01" value={formAchat.quantite} onChange={e => setFormAchat({...formAchat, quantite:e.target.value})} style={{width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Prix unitaire TTC</label>
                <input type="number" step="0.01" value={formAchat.prix_unitaire_ttc} onChange={e => setFormAchat({...formAchat, prix_unitaire_ttc:e.target.value})} style={{width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{display:'block', fontSize:'12px', color:'#6B7280', marginBottom:'4px'}}>Date d'achat</label>
                <input type="date" value={formAchat.date_achat} onChange={e => setFormAchat({...formAchat, date_achat:e.target.value})} style={{width:'100%', padding:'7px 10px', border:'1px solid #D1D5DB', borderRadius:'6px', fontSize:'14px', boxSizing:'border-box'}} />
              </div>
            </div>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'10px'}}>
              <div style={{fontSize:'13px', color:'#6B7280'}}>
                Total : <strong>{(Number(formAchat.quantite||0) * Number(formAchat.prix_unitaire_ttc||0)).toFixed(2)} €</strong> TTC
              </div>
              <div style={{display:'flex', gap:'8px'}}>
                <button onClick={annulerAchat} disabled={savingAchat} style={{background:'#E5E7EB', color:'#374151', border:'none', padding:'7px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'13px'}}>Annuler</button>
                <button onClick={enregistrerAchat} disabled={savingAchat} style={{background:COLORS.brun, color:'white', border:'none', padding:'7px 14px', borderRadius:'6px', cursor:'pointer', fontSize:'13px', fontWeight:'600'}}>{savingAchat ? 'Enregistrement…' : '💾 Enregistrer'}</button>
              </div>
            </div>
          </div>
        )}

        {achats.length === 0
          ? <p style={{color:'#9CA3AF', fontSize:'14px'}}>Aucun achat enregistré pour ce chantier</p>
          : <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                {['Désignation','Fournisseur','Qté','PU TTC','Total TTC','Date',''].map(h => <th key={h} style={s.th(COLORS.brun)}>{h}</th>)}
              </tr></thead>
              <tbody>
                {achats.map((a,i) => (
                  <tr key={a.id} style={{background: i%2===0?'white':'#F9FAFB'}}>
                    <td style={{...s.td, fontWeight:'600'}}>{a.designation}</td>
                    <td style={s.td}>{a.fournisseur||'—'}</td>
                    <td style={s.td}>{parseFloat(a.quantite).toFixed(2)}</td>
                    <td style={s.td}>{parseFloat(a.prix_unitaire_ttc).toFixed(2)} €</td>
                    <td style={{...s.td, fontWeight:'600'}}>{parseFloat(a.total_ttc).toFixed(2)} €</td>
                    <td style={s.td}>{a.date_achat ? new Date(a.date_achat).toLocaleDateString('fr-FR') : '—'}</td>
                    <td style={s.td}>
                      {peutEcrire && <button style={s.btnSm('#6B7280')} onClick={() => ouvrirEditionAchat(a)}>✏️</button>}
                      {peutEcrire && <button style={s.btnSm('#DC2626')} onClick={() => supprimerAchat(a)}>🗑️</button>}
                    </td>
                  </tr>
                ))}
                <tr style={{background:'#FAF7F4', fontWeight:'700'}}>
                  <td style={s.td} colSpan={4}>Total achats</td>
                  <td style={s.td}>{totalAchats.toFixed(2)} €</td>
                  <td style={s.td} colSpan={2}></td>
                </tr>
              </tbody>
            </table>
        }
      </Section>

      <Section titre={`📅 Événements liés (${evenements.length})`} couleur={COLORS.modules.agenda} defaultOpen={false}>
        {evenements.length === 0 ? <p style={{color:'#9CA3AF', fontSize:'14px'}}>Aucun événement lié à cette affaire</p> :
          evenements.map((e,i) => (
            <div key={e.id} style={{display:'flex', alignItems:'center', gap:'12px', padding:'8px 0', borderBottom:'1px solid #F3F4F6'}}>
              <div style={{width:'10px', height:'10px', borderRadius:'50%', background:e.couleur, flexShrink:0}}></div>
              <div style={{flex:1}}>
                <div style={{fontWeight:'600', fontSize:'14px'}}>{e.titre}</div>
                <div style={{fontSize:'12px', color:'#6B7280'}}>{new Date(e.date_debut).toLocaleDateString('fr-FR')}</div>
              </div>
            </div>
          ))
        }
      </Section>
    </div>
  );
}
